import { useCallback, useEffect, useRef, useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import type { BildschirmPhase } from '@k4ia/shared'
import { Avatar } from '../components/Avatar'
import { IntroChatAnsicht } from '../components/IntroChatAnsicht'
import { PoweredBy } from '../components/PoweredBy'
import { SchaufensterQr } from '../components/SchaufensterQr'
import {
  buehneWaehlen,
  fetchIntroChatHistory,
  fetchIntroSpeech,
  fetchShowcases,
  reportIntroScreen,
  introSpeechUrl,
  type IntroChatNachricht,
  type IntroSprachQuelle,
  type IntroZustand,
} from '../lib/api'
import { getAudioContext, resumeAudioContext } from '../lib/audioContext'
import { neuenStandLaden, neuerStandDeployt } from '../lib/stand'

/** Wie oft der Bildschirm seinen Stand meldet und nach Start und Befehlen fragt. */
const POLL_MS = 1000
/**
 * Im Chat öfter: Zwischen „Antwort steht da" und „K4ia spricht" soll möglichst
 * wenig Zeit liegen — der Abruf ist klein und kommt ohne Datenbank aus.
 */
const CHAT_POLL_MS = 400
/** Ohne Tonspur (Entwicklung, ElevenLabs gestört) spielt die Figur so lange stumm. */
const STUMM_MS = 5000
/** So lange ohne Befehl in Boxen oder Chat, dann zurück ins Warten — der Besuch ist wohl weitergezogen. */
const LEERLAUF_MS = 10 * 60_000
/** Wie oft der Bildschirm nachsieht, ob ein neuer Stand deployt ist. */
const STAND_PRUEFEN_MS = 60_000

type Phase = 'bereit' | BildschirmPhase
/** Was gesprochen wird: das Intro, ein Showcase oder eine Antwort aus dem Chat. */
type Tonspur = { art: 'intro' } | { art: 'showcase'; id: string } | { art: 'antwort'; id: string }

const sprachQuelle = (spur: Tonspur): IntroSprachQuelle =>
  spur.art === 'intro'
    ? null
    : spur.art === 'showcase'
      ? { showcaseId: spur.id }
      : { nachricht: spur.id }

interface Box {
  id: string
  titel: string
  /** „powered by": steht unten, solange K4ia zu dieser Box erzählt (leer = kein Hinweis). */
  partnerName: string
  partnerLogoKey: string | null
}

/**
 * `/intro`: der Präsentationsbildschirm.
 *
 * 1. **Wartet:** nur K4ias Figur.
 * 2. **Intro:** Jemand scannt das Intro-Tag und tippt am Handy auf „Intro
 *    starten"; K4ia spricht den im Admin hinterlegten Text.
 * 3. **Boxen:** Danach zeigen Bildschirm und Handy eine Box je Showcase mit
 *    Status „live", in der Reihenfolge aus dem Admin. Wird eine am Handy
 *    angetippt (oder hier angeklickt), erzählt K4ia dazu; eine neue Box
 *    unterbricht die laufende.
 * 4. **Chat:** „Zum Chat" am Handy — hier steht dann das Gespräch, das am
 *    Handy getippt oder gesprochen wird, und K4ia spricht ihre Antworten.
 * 5. Was K4ia gerade spricht, lässt sich vom Handy aus anhalten, fortsetzen
 *    und stoppen. „Beenden" am Handy, Escape hier oder zehn Minuten ohne
 *    Befehl: zurück ins Warten.
 *
 * Der erste Klick ist nicht wegzulassen: Ohne eine Geste am Gerät gibt der
 * Browser keinen Ton frei (Autoplay-Sperre). Wer ihn sparen will, startet
 * Chrome mit `--autoplay-policy=no-user-gesture-required` (docs/SETUP.md).
 */
export function IntroPage({
  modus = 'intro',
  schluessel = '',
  autostart = false,
  startInChat = false,
  stumm = false,
}: {
  /**
   * Gemeinsame Fernbedienung (`/buehne`): Der Klick, der den Ton freigibt,
   * kam schon auf der Bühne — hier sofort starten, ohne eigenen Klick.
   */
  autostart?: boolean
  /**
   * Gemeinsame Fernbedienung im Modus „Gespräch“: Läuft beim Einhängen schon
   * ein Chat (das Handy hat ihn eben geöffnet), gleich in den Chat gehen.
   */
  startInChat?: boolean
  /**
   * Gemeinsame Fernbedienung im Modus „Gespräch“: K4ia antwortet nur im
   * Chat, als Text — sie spricht die Antworten nicht (Flo, 10.10.). Im
   * Intro-Modus („Zum Chat“ vom Handy) spricht sie weiter.
   */
  stumm?: boolean
  /**
   * `schaufenster`: derselbe Bildschirm im Fenster nach draußen — mit
   * wechselndem QR-Code statt Tag, ohne Klick zum Start, und im Chat nur mit
   * K4ias Antworten (der Worker liefert die Fragen gar nicht erst).
   */
  modus?: 'intro' | 'schaufenster'
  /** Schaufenster: der Schlüssel aus `?k=` (steht im Admin). */
  schluessel?: string
} = {}) {
  // Vor allen Abrufen: welche Bühne, und mit welchem Schlüssel.
  useState(() => buehneWaehlen(modus, schluessel))
  const schaufenster = modus === 'schaufenster'
  const [phase, setPhaseState] = useState<Phase>('bereit')
  const [thema, setThemaState] = useState<string | null>(null)
  const [spricht, setSprichtState] = useState(false)
  const [pausiert, setPausiertState] = useState(false)
  const [boxen, setBoxen] = useState<Box[]>([])
  const [chat, setChat] = useState<IntroChatNachricht[]>([])
  const [pegel, setPegel] = useState(0)
  const [hinweis, setHinweis] = useState<string | null>(null)
  const groesse = useAvatarGroesse()
  const reduceMotion = useReducedMotion()

  // Die Abfrage läuft im Intervall und braucht den aktuellen Stand, nicht den
  // vom Zeitpunkt, als sie angelegt wurde — daher alles zusätzlich als Ref.
  const phaseRef = useRef<Phase>(phase)
  const themaRef = useRef<string | null>(thema)
  const sprichtRef = useRef(false)
  const pausiertRef = useRef(false)
  const setPhase = (p: Phase) => {
    phaseRef.current = p
    setPhaseState(p)
  }
  const setThema = (t: string | null) => {
    themaRef.current = t
    setThemaState(t)
  }
  const setSpricht = (s: boolean) => {
    sprichtRef.current = s
    setSprichtState(s)
  }
  const setPausiert = (p: boolean) => {
    pausiertRef.current = p
    setPausiertState(p)
  }

  const startInChatRef = useRef(startInChat)
  startInChatRef.current = startInChat
  const stummRef = useRef(stumm)
  stummRef.current = stumm

  /** Bis wohin Scans und Befehle schon behandelt sind; `null` = noch kein Stand gesehen. */
  const gesehen = useRef<{ zaehler: number; befehl: number } | null>(null)
  const letzterBefehlAm = useRef(Date.now())
  /** Chat-Antworten, die schon gesprochen (oder beim Öffnen schon da) waren. */
  const gesprochen = useRef(new Set<string>())
  /** Ist der Ausgangsstand des Chats schon gelesen? Erst danach wird Neues gesprochen. */
  const chatGelesen = useRef(false)
  /**
   * Das Audio-Element für die gestreamte Stimme. Angelegt und einmal (still)
   * abgespielt beim ersten Klick ins Bild — Safari und strenge Browser-
   * Einstellungen lassen ein Element sonst nur direkt nach einem Klick
   * spielen, hier aber kommt der Befehl Sekunden später vom Handy. Danach wird
   * es für jede Ansage wiederverwendet.
   */
  const element = useRef<{ audio: HTMLAudioElement; analyser: AnalyserNode } | null>(null)
  /**
   * Die laufende Wiedergabe: über das Element (gestreamt) oder, wenn der
   * Browser das verweigert, als vorher geladene Datei über den AudioContext.
   * `id` steigt mit jedem Start, damit eine unterbrochene nichts mehr umstellt.
   */
  const spieler = useRef<{
    weg: 'element' | 'puffer' | null
    quelle: AudioBufferSourceNode | null
    id: number
  }>({ weg: null, quelle: null, id: 0 })

  /** Holt eine Tonspur vorab, damit sie beim ersten Abspielen schon im Speicher liegt. */
  const ladeTonspur = useCallback(async (spur: Tonspur) => {
    await fetchIntroSpeech(sprachQuelle(spur))
  }, [])

  const stoppen = useCallback(() => {
    const { weg, quelle } = spieler.current
    spieler.current = { weg: null, quelle: null, id: spieler.current.id + 1 }
    const audio = element.current?.audio
    if (weg === 'element' && audio) {
      audio.pause()
      audio.removeAttribute('src')
      audio.load()
    }
    quelle?.stop()
    // Pausiert wird der Puffer-Weg über den Context — der muss wieder laufen.
    if (pausiertRef.current && weg === 'puffer') void resumeAudioContext().catch(() => undefined)
    setPegel(0)
    setSpricht(false)
    setPausiert(false)
  }, [])

  /** Misst den Pegel für den Schein hinter der Figur; gibt die Funktion zum Aufhören zurück. */
  const pegelMessen = useCallback((analyser: AnalyserNode) => {
    const werte = new Float32Array(analyser.fftSize)
    let frame = 0
    const messen = () => {
      analyser.getFloatTimeDomainData(werte)
      let summe = 0
      for (const w of werte) summe += w * w
      setPegel(Math.min(1, Math.sqrt(summe / werte.length) * 4))
      frame = requestAnimationFrame(messen)
    }
    messen()
    return () => cancelAnimationFrame(frame)
  }, [])

  /** Gestreamt über das freigeschaltete Element: spielt beim ersten Takt. */
  const spieleElement = useCallback(
    (el: { audio: HTMLAudioElement; analyser: AnalyserNode }, url: string) => {
      spieler.current.weg = 'element'
      el.audio.src = url
      const aufhoeren = pegelMessen(el.analyser)
      return new Promise<boolean>((ende, fehler) => {
        el.audio.onended = () => ende(true)
        el.audio.onerror = () => fehler(new Error('Tonspur nicht abspielbar'))
        el.audio.onplaying = () => setHinweis(null)
        void el.audio.play().catch(fehler)
      }).finally(aufhoeren)
    },
    [pegelMessen],
  )

  /** Der sichere Weg: ganze Datei laden, über den AudioContext abspielen. Klappt überall, streamt aber nicht. */
  const spielePuffer = useCallback(
    async (ctx: AudioContext, spur: Tonspur, aktuell: () => boolean) => {
      spieler.current.weg = 'puffer'
      const { audio } = await fetchIntroSpeech(sprachQuelle(spur))
      const buffer = await ctx.decodeAudioData(audio)
      if (!aktuell()) return false
      setHinweis(null)
      const analyser = ctx.createAnalyser()
      analyser.fftSize = 1024
      analyser.connect(ctx.destination)
      const quelle = ctx.createBufferSource()
      quelle.buffer = buffer
      quelle.connect(analyser)
      spieler.current.quelle = quelle
      const aufhoeren = pegelMessen(analyser)
      return new Promise<boolean>((ende) => {
        quelle.onended = () => ende(aktuell())
        quelle.start()
      }).finally(aufhoeren)
    },
    [pegelMessen],
  )

  /**
   * Spielt eine Tonspur; `true`, wenn sie zu Ende lief, `false`, wenn etwas
   * anderes sie abgelöst hat.
   *
   * Zuerst gestreamt über das Audio-Element — der Worker reicht die Stimme von
   * ElevenLabs gleich weiter, K4ia beginnt nach Bruchteilen einer Sekunde.
   * Verweigert der Browser das (Safari, strenge Ton-Einstellungen), dann über
   * den AudioContext mit der ganzen Datei: später, aber zuverlässig.
   */
  const sprechen = useCallback(
    async (spur: Tonspur): Promise<boolean> => {
      stoppen()
      const id = spieler.current.id
      const aktuell = () => spieler.current.id === id
      setSpricht(true)
      try {
        const ctx = await resumeAudioContext()
        let fertig: boolean
        const el = element.current
        if (el) {
          try {
            fertig = await spieleElement(el, introSpeechUrl(sprachQuelle(spur)))
          } catch (e) {
            if (!aktuell()) return false
            console.warn('Intro: gestreamte Wiedergabe abgelehnt, spiele die ganze Datei', e)
            el.audio.removeAttribute('src')
            fertig = await spielePuffer(ctx, spur, aktuell)
          }
        } else {
          fertig = await spielePuffer(ctx, spur, aktuell)
        }
        if (!aktuell()) return false
        spieler.current = { weg: null, quelle: null, id }
        setPegel(0)
        setSpricht(false)
        return fertig
      } catch (e) {
        // Kein Ton ist kein Grund, die Vorführung zu stoppen: Die Figur spielt
        // stumm, und in der Ecke steht, warum.
        if (!aktuell()) return false
        setHinweis(`Sprachausgabe nicht verfügbar: ${e instanceof Error ? e.message : String(e)}`)
        await new Promise((r) => setTimeout(r, STUMM_MS))
        if (!aktuell()) return false
        setSpricht(false)
        return true
      }
    },
    [stoppen, spieleElement, spielePuffer],
  )

  const introSpielen = useCallback(async () => {
    letzterBefehlAm.current = Date.now()
    setPhase('intro')
    setThema(null)
    // Neuer Besuch: nichts vom vorigen Gespräch und keine alte Fehlermeldung.
    setChat([])
    setHinweis(null)
    // Die Boxen gleich mitladen, damit sie nach dem Intro ohne Wartezeit dastehen.
    void fetchShowcases()
      .then((r) =>
        setBoxen(
          r.showcases.map((s) => ({
            id: s.id,
            titel: s.title,
            partnerName: s.partnerName,
            partnerLogoKey: s.partnerLogoKey,
          })),
        ),
      )
      .catch(() => undefined)
    if (await sprechen({ art: 'intro' })) setPhase('menue')
  }, [sprechen])

  const themaSpielen = useCallback(
    async (id: string) => {
      letzterBefehlAm.current = Date.now()
      setPhase('thema')
      setThema(id)
      if (await sprechen({ art: 'showcase', id })) {
        setPhase('menue')
        setThema(null)
      }
    },
    [sprechen],
  )

  const zuDenBoxen = useCallback(() => {
    stoppen()
    setThema(null)
    setPhase('menue')
  }, [stoppen])

  const chatOeffnen = useCallback(() => {
    stoppen()
    setThema(null)
    // Jedes „Zum Chat" ist ein neues Gespräch — der alte Verlauf blitzt nicht auf.
    setChat([])
    chatGelesen.current = false
    setPhase('chat')
  }, [stoppen])

  const beenden = useCallback(() => {
    stoppen()
    setThema(null)
    setChat([])
    setHinweis(null)
    setPhase('wartet')
  }, [stoppen])

  const pausieren = useCallback(() => {
    const { weg } = spieler.current
    if (!weg || pausiertRef.current) return
    if (weg === 'element') element.current?.audio.pause()
    else void getAudioContext().suspend()
    setPausiert(true)
  }, [])

  const fortsetzen = useCallback(() => {
    const { weg } = spieler.current
    if (!weg || !pausiertRef.current) return
    const weiter =
      weg === 'element'
        ? (element.current?.audio.play() ?? Promise.resolve())
        : resumeAudioContext().then(() => undefined)
    void weiter.then(() => setPausiert(false)).catch(() => undefined)
  }, [])

  /** Stopp: die laufende Ansage ab. Aus Intro und Box geht es zu den Boxen, der Chat bleibt. */
  const anhalten = useCallback(() => {
    if (phaseRef.current === 'chat') stoppen()
    else zuDenBoxen()
  }, [stoppen, zuDenBoxen])

  const verarbeiten = useCallback(
    (zustand: IntroZustand) => {
      const p = phaseRef.current
      const vorher = gesehen.current
      gesehen.current = { zaehler: zustand.zaehler, befehl: zustand.befehl.nr }
      // Der erste Stand nach dem Aktivieren ist nur der Ausgangspunkt — außer
      // auf der Bühne im Modus „Gespräch“: Dort hat das Handy den Chat schon
      // geöffnet, bevor dieser Bildschirm eingehängt war.
      if (!vorher) {
        if (startInChatRef.current && zustand.chatSitzung) chatOeffnen()
        return
      }

      if (p === 'wartet') {
        // Scans zählen nur hier; Befehle ohne Handy-Besuch verfallen — bis auf
        // „Zum Chat“ von der gemeinsamen Fernbedienung (Modus „Gespräch“): Das
        // Gespräch braucht kein Intro davor.
        if (zustand.zaehler > vorher.zaehler) void introSpielen()
        else if (
          !schaufenster &&
          zustand.befehl.nr > vorher.befehl &&
          zustand.befehl.art === 'chat' &&
          zustand.chatSitzung
        ) {
          letzterBefehlAm.current = Date.now()
          chatOeffnen()
        }
        return
      }
      if (zustand.befehl.nr > vorher.befehl) {
        letzterBefehlAm.current = Date.now()
        const befehl = zustand.befehl
        switch (befehl.art) {
          case 'thema':
            void themaSpielen(befehl.thema)
            return
          case 'pause':
            return pausieren()
          case 'weiter':
            return fortsetzen()
          case 'stopp':
            return anhalten()
          case 'chat':
            return chatOeffnen()
          case 'boxen':
            return zuDenBoxen()
          case 'ende':
            return beenden()
        }
      }
      if (
        (p === 'menue' || p === 'chat') &&
        !sprichtRef.current &&
        Date.now() - letzterBefehlAm.current > LEERLAUF_MS
      ) {
        beenden()
      }
    },
    [introSpielen, themaSpielen, pausieren, fortsetzen, anhalten, chatOeffnen, zuDenBoxen, beenden],
  )

  // Melden und nachfragen in einem: Die Meldung ist zugleich das Lebenszeichen,
  // an dem das Handy sieht, dass ein Bildschirm verbunden ist.
  useEffect(() => {
    if (phase === 'bereit') return
    let aktiv = true
    const takt = async () => {
      const p = phaseRef.current
      if (p === 'bereit') return
      try {
        const zustand = await reportIntroScreen({
          phase: p,
          thema: themaRef.current,
          spricht: sprichtRef.current,
          pausiert: pausiertRef.current,
        })
        if (aktiv) verarbeiten(zustand)
      } catch {
        // Netz kurz weg: beim nächsten Durchlauf wieder
      }
    }
    void takt()
    const id = setInterval(() => void takt(), POLL_MS)
    return () => {
      aktiv = false
      clearInterval(id)
    }
    // Einmal nach dem Aktivieren; alles Weitere liest takt() aus den Refs.
  }, [phase === 'bereit'])

  // Im Chat: das Gespräch vom Handy mitlesen und jede neue Antwort sprechen.
  useEffect(() => {
    if (phase !== 'chat') return
    let aktiv = true
    const lesen = async () => {
      try {
        const { nachrichten } = await fetchIntroChatHistory()
        if (!aktiv) return
        setChat(nachrichten)
        const antworten = nachrichten.filter((n) => n.role === 'assistant' && n.content)
        if (!chatGelesen.current) {
          // Was beim Öffnen schon dasteht, ist gesagt — nur Neues wird gesprochen.
          for (const a of antworten) gesprochen.current.add(a.id)
          chatGelesen.current = true
          return
        }
        const neu = antworten.filter((a) => !gesprochen.current.has(a.id)).at(-1)
        if (neu) {
          for (const a of antworten) gesprochen.current.add(a.id)
          letzterBefehlAm.current = Date.now()
          // Stumm (Bühne, Modus Gespräch): Die Antwort steht im Chat, mehr
          // nicht — und gilt als gesagt, damit sie nach einem Wechsel ins
          // Intro nicht nachträglich gesprochen wird.
          if (!stummRef.current) void sprechen({ art: 'antwort', id: neu.id })
        }
      } catch {
        // Netz kurz weg: beim nächsten Durchlauf wieder
      }
    }
    void lesen()
    const id = setInterval(() => void lesen(), CHAT_POLL_MS)
    return () => {
      aktiv = false
      clearInterval(id)
    }
  }, [phase, sprechen])

  // Ein neuer Stand ist deployt: neu laden — aber nur, solange niemand da ist.
  // Mitten in einer Vorführung wartet das, bis der Bildschirm wieder wartet.
  // Danach will der Browser einmal einen Klick für den Ton; das Handy sagt
  // dann „Der Bildschirm ist nicht verbunden … klicke einmal hinein".
  useEffect(() => {
    const id = setInterval(() => {
      if (phaseRef.current !== 'wartet' && phaseRef.current !== 'bereit') return
      void neuerStandDeployt()
        .then((neu) => {
          if (neu) void neuenStandLaden()
        })
        .catch(() => undefined)
    }, STAND_PRUEFEN_MS)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && phaseRef.current !== 'bereit') beenden()
    }
    window.addEventListener('keydown', taste)
    return () => window.removeEventListener('keydown', taste)
  }, [beenden])

  // Schaufenster: Niemand klickt — gleich starten. Den Ton gibt dort die
  // Kiosk-Einstellung des Browsers frei, nicht ein Klick. Auf der Bühne kam
  // der Klick schon vorher.
  useEffect(() => {
    if (schaufenster || autostart) void aktivieren()
    // Einmal beim Einhängen.
  }, [])

  // Auf der Bühne wird dieser Bildschirm beim Wechsel ausgehängt: Was K4ia
  // gerade sagt, hört dann auf, statt unter dem Gewinnspiel weiterzulaufen.
  useEffect(() => {
    return () => stoppen()
  }, [stoppen])

  // Ohne Kiosk-Einstellung tut es zur Not ein Klick ins Bild (beim Einrichten
  // oder Testen): der gibt Ton und Audio-Element frei.
  useEffect(() => {
    if (!schaufenster) return
    const freigeben = () => {
      void resumeAudioContext()
        .then((ctx) => {
          if (ctx.state === 'running') {
            setHinweis((h) => (h?.startsWith('Ton gesperrt') ? null : h))
          }
        })
        .catch(() => undefined)
      const audio = element.current?.audio
      if (audio && !spieler.current.weg) {
        audio.src = stilleWav()
        void audio.play().catch(() => undefined)
      }
    }
    window.addEventListener('pointerdown', freigeben)
    return () => window.removeEventListener('pointerdown', freigeben)
  }, [schaufenster])

  async function aktivieren() {
    // Innerhalb des Klicks: nur so gibt der Browser den Ton frei — für den
    // AudioContext und für das Audio-Element, das später die Stimme streamt.
    const ctx = getAudioContext()
    if (!element.current) {
      const audio = new Audio()
      audio.src = stilleWav()
      // Im Schaufenster ohne Kiosk-Einstellung gesperrt — der Hinweis kommt unten.
      void audio.play().catch(() => undefined)
      const analyser = ctx.createAnalyser()
      analyser.fftSize = 1024
      ctx.createMediaElementSource(audio).connect(analyser)
      analyser.connect(ctx.destination)
      element.current = { audio, analyser }
    }
    // Nicht unbegrenzt warten: Ohne Klick (Schaufenster ohne Kiosk-Einstellung)
    // bleibt die Zusage von resume() einfach offen — die Seite blieb schwarz.
    await Promise.race([
      resumeAudioContext().catch(() => undefined),
      new Promise((fertig) => setTimeout(fertig, 500)),
    ])
    if (schaufenster && getAudioContext().state !== 'running') {
      setHinweis(
        'Ton gesperrt: Chrome im Schaufenster mit --autoplay-policy=no-user-gesture-required starten (docs/SETUP.md) — oder einmal ins Bild klicken.',
      )
    }
    void document.documentElement.requestFullscreen?.().catch(() => undefined)
    gesehen.current = null
    setPhase('wartet')
    // Schon jetzt holen, damit das erste Intro nicht auf ElevenLabs wartet.
    void ladeTonspur({ art: 'intro' }).catch((e: unknown) =>
      setHinweis(`Sprachausgabe nicht verfügbar: ${e instanceof Error ? e.message : String(e)}`),
    )
  }

  if (phase === 'bereit') {
    // Im Schaufenster startet der Bildschirm von selbst (siehe Effekt oben).
    if (schaufenster) return <main className="min-h-screen bg-bg" />
    return (
      <button
        onClick={() => void aktivieren()}
        className="flex min-h-screen w-full flex-col items-center justify-center gap-8 bg-bg p-12 text-center"
      >
        <Avatar state="idle" size={Math.round(groesse * 0.6)} showLabel={false} />
        <div>
          <h1 className="text-3xl font-semibold">Intro-Bildschirm</h1>
          <p className="mt-3 text-lg text-text-muted">
            Einmal klicken, um den Ton freizugeben — danach startet das Intro, sobald am Handy
            „Intro starten“ getippt wird.
          </p>
        </div>
      </button>
    )
  }

  const mitBoxen = phase === 'menue' || phase === 'thema'
  const imChat = phase === 'chat'
  const raster = boxenRaster(boxen.length)
  const letzte = chat.at(-1)
  const avatarZustand = spricht
    ? pausiert
      ? 'idle'
      : 'speaking'
    : imChat && letzte?.role === 'user'
      ? 'thinking'
      : 'idle'
  // Drei Reihen Boxen oder der Chat brauchen Platz: Die Figur wird dann kleiner.
  // Überschrift, Figur und bis zu drei Reihen Boxen müssen auf einen Bildschirm passen.
  const mitQr = schaufenster && phase === 'wartet'
  const figur = mitBoxen
    ? Math.round(groesse * (raster.reihen >= 3 ? 0.36 : 0.45))
    : Math.round(groesse * (mitQr ? 0.7 : 0.85))
  const auftritt = spricht && !pausiert

  const hinweisZeile = hinweis && (
    <p className="fixed bottom-3 left-3 max-w-md text-xs text-text-muted" role="status">
      {hinweis}
    </p>
  )

  if (imChat) {
    return (
      <>
        <IntroChatAnsicht nachrichten={chat} avatarZustand={avatarZustand} pausiert={pausiert} />
        {hinweisZeile}
      </>
    )
  }

  const figurBlock = (
    <div className="flex flex-col items-center gap-3">
      <motion.div
        className="relative flex items-center justify-center"
        // Warten: sanftes Schweben. Einsatz: ein kurzer Auftritt nach vorn.
        animate={
          reduceMotion
            ? { scale: 1, y: 0 }
            : auftritt
              ? { scale: [0.9, 1.08, 1], y: 0 }
              : { scale: 1, y: [0, -12, 0] }
        }
        transition={
          reduceMotion
            ? { duration: 0 }
            : auftritt
              ? { duration: 0.9, ease: 'easeOut' }
              : { duration: 6, repeat: Infinity, ease: 'easeInOut' }
        }
      >
        {/* Ein Schein hinter der Figur, der mit ihrer Stimme mitgeht. */}
        <motion.div
          aria-hidden
          className="absolute rounded-full"
          style={{
            width: figur * 1.4,
            height: figur * 1.4,
            background: 'radial-gradient(circle, var(--color-accent) 0%, transparent 65%)',
          }}
          animate={{
            scale: spricht && !pausiert ? 1 + pegel * 0.35 : 0.9,
            opacity: spricht && !pausiert ? 0.25 + pegel * 0.55 : 0.12,
          }}
          transition={{ duration: 0.12, ease: 'easeOut' }}
        />
        <Avatar state={avatarZustand} size={figur} showLabel={false} />
      </motion.div>
      {pausiert && (
        <p className="rounded-full border border-border px-4 py-1 text-lg text-text-muted">
          Pausiert
        </p>
      )}
    </div>
  )

  return (
    <main
      className={`overflow-hidden bg-bg ${'flex min-h-screen flex-col items-center justify-center gap-8 p-8'} ${mitBoxen ? '' : 'cursor-none'}`}
    >
      {/* Brooklyn Bold über die h1-Regel in global.css (ADR 0011). */}
      <motion.h1
        className={`text-center leading-none ${mitBoxen ? 'text-5xl' : 'text-7xl'}`}
        initial={reduceMotion ? false : { opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        Hallo! Ich bin K4ia.
      </motion.h1>
      {mitQr ? (
        // Schaufenster, niemand steuert: Figur und QR-Code nebeneinander.
        <div className="flex items-center justify-center gap-24">
          {figurBlock}
          <SchaufensterQr groesse={Math.round(groesse * 0.55)} />
        </div>
      ) : (
        figurBlock
      )}

      {mitBoxen && (
        <motion.ul
          className="flex w-full max-w-6xl flex-wrap justify-center gap-5"
          initial={reduceMotion ? false : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          {boxen.map((box) => (
            <li
              key={box.id}
              // Feste Breite je Spalte statt eines Grids: Geht die Anzahl nicht
              // auf, steht die letzte Reihe mittig statt linksbündig.
              style={{
                width: `calc((100% - ${raster.spalten - 1} * 1.25rem) / ${raster.spalten})`,
              }}
            >
              <button
                onClick={() => void themaSpielen(box.id)}
                aria-pressed={thema === box.id}
                className={`flex h-28 w-full items-center justify-center rounded-2xl border-2 px-5 text-center text-xl font-semibold leading-tight transition ${
                  thema === box.id
                    ? 'border-[var(--color-accent)] bg-bg-elevated shadow-[0_0_32px_var(--color-accent)]'
                    : 'border-border bg-bg-elevated'
                }`}
              >
                {box.titel}
              </button>
            </li>
          ))}
        </motion.ul>
      )}

      {/* „powered by" unten, solange K4ia zu einer Box mit Partner erzählt. */}
      {phase === 'thema' && (
        <motion.div
          key={thema ?? ''}
          initial={reduceMotion ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          <PoweredBy showcase={boxen.find((b) => b.id === thema)} gross />
        </motion.div>
      )}

      {hinweisZeile}
    </main>
  )
}

/**
 * Wie viele Boxen nebeneinander, damit die Reihen gleich lang sind: 4 → 2 × 2,
 * 6 → 2 × 3, 8 → 2 × 4, 9 → 3 × 3, 12 → 3 × 4. Geht die Anzahl weder in zwei
 * noch in drei Reihen auf (5, 7, 11 …), bleibt die letzte Reihe kürzer und
 * steht mittig.
 */
export function boxenRaster(anzahl: number): { spalten: number; reihen: number } {
  if (anzahl <= 3) return { spalten: Math.max(anzahl, 1), reihen: 1 }
  for (const reihen of [2, 3]) {
    const spalten = anzahl / reihen
    if (Number.isInteger(spalten) && spalten <= 5) return { spalten, reihen }
  }
  const reihen = anzahl <= 8 ? 2 : 3
  return { spalten: Math.ceil(anzahl / reihen), reihen }
}

/**
 * Ein Zwanzigstel Sekunde Stille als WAV — zum Freischalten des Audio-Elements
 * beim ersten Klick, ohne dass etwas zu hören ist.
 */
export function stilleWav(): string {
  const proben = 400
  const puffer = new ArrayBuffer(44 + proben * 2)
  const v = new DataView(puffer)
  const text = (pos: number, t: string) => {
    for (let i = 0; i < t.length; i++) v.setUint8(pos + i, t.charCodeAt(i))
  }
  text(0, 'RIFF')
  v.setUint32(4, 36 + proben * 2, true)
  text(8, 'WAVE')
  text(12, 'fmt ')
  v.setUint32(16, 16, true)
  v.setUint16(20, 1, true)
  v.setUint16(22, 1, true)
  v.setUint32(24, 8000, true)
  v.setUint32(28, 16000, true)
  v.setUint16(32, 2, true)
  v.setUint16(34, 16, true)
  text(36, 'data')
  v.setUint32(40, proben * 2, true)
  return URL.createObjectURL(new Blob([puffer], { type: 'audio/wav' }))
}

/** Die Figur füllt gut die Hälfte des Bildschirms, auch nach dem Wechsel in den Vollbildmodus. */
function useAvatarGroesse(): number {
  const messen = () => Math.round(Math.min(window.innerWidth, window.innerHeight) * 0.55)
  const [groesse, setGroesse] = useState(messen)
  useEffect(() => {
    const neu = () => setGroesse(messen())
    window.addEventListener('resize', neu)
    return () => window.removeEventListener('resize', neu)
  }, [])
  return groesse
}
